import { type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PartialConfigCategory, SwarmType } from '@/graphql/types';
import { useObservableState, useSubscription } from 'observable-hooks';
import { ErrorBoundary } from 'react-error-boundary';
import Form from '../../reactForm/Form';
import { Layout } from '../../reactForm/Layout';
import { SaveLoadingButton } from '../../reactForm/SaveLoadingButton';
import { ModalForm, ModalFormBody } from './Modal';
import { BasePage } from '../BasePage';
import {
    NOTIFICATION_VARIANT,
    NotificationsContainer,
    NotificationsProvider,
    useNotifications,
} from '../../notifications/Notifications';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { createPartialConfigEditUrl, usePartialConfigPageTitle } from './utils';
import { PartialConfigController } from './PartialConfigController';
import { CloseButton, PartialConfigErrorFallback } from './PartialConfigFormErrors';
import { usePartialConfigOnClose } from './partialConfigModalUtils';
import { PartialConfigModalFooter } from './PartialConfigModalFooter';

function PartialConfigFormContentInner({
    swarmType,
    partialConfigType,
    id,
}: {
    swarmType: SwarmType;
    partialConfigType: PartialConfigCategory;
    id?: string;
}) {
    const formController = PartialConfigController.use(swarmType, false, {
        partialConfigType,
        id,
    });

    const isLoading = useObservableState(formController.loading$, false);

    const isEditMode = Boolean(id);
    const { addNotification } = useNotifications();
    const navigate = useNavigate();
    const title = usePartialConfigPageTitle(swarmType, partialConfigType, Boolean(id));
    const onClose = usePartialConfigOnClose();

    useSubscription(formController.onSubmitSuccessful$, ({ id: idFromAPI }) => {
        const createdId = String(idFromAPI);

        if (!isEditMode) {
            navigate(createPartialConfigEditUrl(createdId, swarmType, partialConfigType));
        }

        addNotification({
            message: gettext('SAVED_SUCCESSFULLY'),
            variant: NOTIFICATION_VARIANT.SUCCESS,
            scrollToTop: true,
        });
    });

    return (
        <>
            <ModalFormBody>
                <BasePage
                    title={title}
                    besideTitle={<CloseButton onClose={onClose} />}
                    wrapMainContainer={false}
                    forModalUsage={true}
                >
                    <NotificationsContainer />
                    <Form formController={formController} showSaveButton={false}>
                        {isLoading && (
                            <div
                                className="mb-4 flex items-center gap-2 text-sm text-gray-500"
                                role="status"
                            >
                                <LoadingSpinner className="h-4 text-gray-400" />
                                <span>{gettext('LOADING_PARTIAL_CONFIGURATION')}</span>
                            </div>
                        )}
                        <div className={isLoading ? 'pointer-events-none opacity-50' : undefined}>
                            <Layout formController={formController} />
                        </div>
                    </Form>
                </BasePage>
            </ModalFormBody>
            <PartialConfigModalFooter>
                <SaveLoadingButton
                    formController={formController}
                    type="button"
                    className="px-4 py-2 text-sm"
                    onClick={(e: MouseEvent) => void formController.triggerSubmit(e)}
                />
            </PartialConfigModalFooter>
        </>
    );
}

export function PartialConfigForm({
    swarmType,
    partialConfigType,
    id,
}: {
    swarmType: SwarmType;
    partialConfigType: PartialConfigCategory;
    id?: string;
}) {
    const onClose = usePartialConfigOnClose();
    const key = `${swarmType}_${partialConfigType}_${id ?? 'create'}`;

    return (
        <ModalForm onClose={onClose}>
            <ErrorBoundary FallbackComponent={PartialConfigErrorFallback}>
                <NotificationsProvider>
                    <PartialConfigFormContentInner
                        key={key}
                        swarmType={swarmType}
                        partialConfigType={partialConfigType}
                        id={id}
                    />
                </NotificationsProvider>
            </ErrorBoundary>
        </ModalForm>
    );
}
